// Background blur for the local camera.
//
// camera MediaStream -> hidden <video> -> per-frame person segmentation
// (MediaPipe ImageSegmenter) + canvas compositing -> canvas.captureStream().
//
// The returned stream is used exactly like the raw camera stream (preview,
// own tile, Janus publish), so nothing else in the call needs to know about it.
// Stopping the returned video track also releases the camera.
//
// Gated by AccountRole :background_blur (see app/views/live_calls/join/_video.html.erb).

// Served from our own origin: wasm is copied by webpack.config.js
// (MEDIAPIPE_WASM_PATH), the model is committed under public/mediapipe.
const WASM_PATH = typeof MEDIAPIPE_WASM_PATH !== 'undefined' ? MEDIAPIPE_WASM_PATH : '/packs/mediapipe';
const MODEL_PATH = '/mediapipe/selfie_segmenter_landscape.tflite';

const FRAME_RATE = 24;            // Matches the TWO_PARTIES encoding cap in video_connection.js
const SEGMENT_WIDTH = 256;        // The landscape model's native input size
const SEGMENT_HEIGHT = 144;
const BLUR_SCALE = 0.25;          // The background is blurred at quarter resolution
const BLUR_RADIUS_PX = 4;         // At quarter resolution, roughly 16px at full size
const MASK_FEATHER_PX = 1;        // Softens the person/background edge (at mask resolution)
// Without canvas filters (Safari, including 26.x) the background is blurred
// by halving it this many times and scaling it back up with smoothing:
// 4 halvings of the quarter-size background is 1/64 of full size, close to
// the filter path's strength
const SCALE_BLUR_STEPS = 4;
const FRAME_INTERVAL_MS = Math.round(1000 / FRAME_RATE);
const OVERLOAD_MS = 50;           // Average per-frame cost that counts as too slow...
const OVERLOAD_WINDOW_MS = 3000;  // ...over the last 3 seconds
const START_TIMEOUT_MS = 20000;   // First use downloads ~13MB of wasm; don't hold up joining

// A timer in a Web Worker keeps ticking when the tab is in the background,
// where requestAnimationFrame and main-thread timers are paused or throttled.
// Without it the published video freezes when someone switches tabs.
const TIMER_WORKER_SOURCE = `
  let timer = null;
  onmessage = (event) => {
    clearInterval(timer);
    timer = null;
    if (event.data && event.data.interval) {
      timer = setInterval(() => postMessage('tick'), event.data.interval);
    }
  };
`;

// One segmenter per page: loading the wasm and model is the slow part.
let segmenterPromise = null;

async function loadSegmenter() {
    if (!segmenterPromise) {
        segmenterPromise = (async () => {
            const { FilesetResolver, ImageSegmenter } = await import(/* webpackChunkName: "mediapipe-vision" */ '@mediapipe/tasks-vision');
            const fileset = await FilesetResolver.forVisionTasks(WASM_PATH);
            const options = (delegate) => ({
                baseOptions: { modelAssetPath: MODEL_PATH, delegate },
                runningMode: 'VIDEO',
                outputConfidenceMasks: true,
                outputCategoryMask: false
            });
            try {
                return await ImageSegmenter.createFromOptions(fileset, options('GPU'));
            } catch (gpuError) {
                console.warn('[BackgroundBlur] GPU delegate unavailable, using CPU', gpuError);
                return await ImageSegmenter.createFromOptions(fileset, options('CPU'));
            }
        })();
        // Let a later attempt retry after a failure (e.g. a dropped download)
        segmenterPromise.catch(() => { segmenterPromise = null; });
    }
    return segmenterPromise;
}

function createCanvas(width, height) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
}

export default class BackgroundBlur {
    static isSupported() {
        try {
            return typeof HTMLCanvasElement !== 'undefined' &&
                typeof HTMLCanvasElement.prototype.captureStream === 'function' &&
                typeof WebAssembly === 'object' &&
                typeof Worker === 'function' &&
                typeof CanvasRenderingContext2D !== 'undefined';
        } catch (e) {
            return false;
        }
    }

    // Canvas 2D `filter` (Chrome, Edge, Firefox; not Safari as of 26.4).
    // Without it the blur uses scaling instead (see blurByScaling).
    static hasCanvasFilter() {
        try {
            return 'filter' in CanvasRenderingContext2D.prototype;
        } catch (e) {
            return false;
        }
    }

    // onFallback(reason): called once if blur stops working well enough to use
    // ("overload": the device can't keep up, "error": frames keep failing), so
    // the caller can switch back to the plain camera.
    constructor({ onFallback } = {}) {
        this.onFallback = onFallback;
        this.framesRendered = 0;
        this.consecutiveErrors = 0;
        this.cameraStream = null;
        this.outputStream = null;
        this.stopped = false;
        this.busy = false;
        this.frameCosts = []; // [timestamp, ms] for the last OVERLOAD_WINDOW_MS
        this.lastFrameEnd = 0;
        this.lastTimestamp = 0;
    }

    // Resolves with the blurred stream. Rejects if blur can't run here; the
    // camera stream is left untouched in that case so the caller can use it.
    async start(cameraStream) {
        this.cameraStream = cameraStream;
        let timer;
        const timeout = new Promise((_, reject) => {
            timer = setTimeout(() => reject(new Error('Background blur took too long to start')), START_TIMEOUT_MS);
        });
        try {
            return await Promise.race([this.setUp(cameraStream), timeout]);
        } catch (error) {
            // Tidy up whatever was set up, but leave the camera for the caller.
            // setUp checks `stopped` after each await, so a timed-out start
            // doesn't carry on in the background.
            this.stop({ keepCamera: true });
            throw error;
        } finally {
            clearTimeout(timer);
        }
    }

    // keepCamera: stop blurring but leave the camera running (used when the
    // caller swaps the raw camera track back into the call).
    stop({ keepCamera = false } = {}) {
        if (this.stopped) return;
        this.stopped = true;
        if (this.timer) {
            this.timer.postMessage({ interval: 0 });
            this.timer.terminate();
            this.timer = null;
        }
        if (this.timerUrl) {
            URL.revokeObjectURL(this.timerUrl);
            this.timerUrl = null;
        }
        if (this.stopOutputTrack) this.stopOutputTrack();
        if (this.video) {
            this.video.srcObject = null;
            this.video.remove();
            this.video = null;
        }
        if (!keepCamera && this.cameraStream) {
            this.cameraStream.getTracks().forEach((track) => track.stop());
        }
    }

    // --- internals ----------------------------------------------------------

    async setUp(cameraStream) {
        const segmenter = await loadSegmenter();
        this.throwIfStopped();
        this.segmenter = segmenter;


        this.video = document.createElement('video');
        this.video.muted = true;
        this.video.playsInline = true;
        this.video.autoplay = true;
        // Kept in the DOM (Safari stops updating detached videos) but invisible.
        // display:none would also stop frame updates in some browsers.
        Object.assign(this.video.style, {
            position: 'fixed', left: '-10px', top: '-10px', width: '1px', height: '1px',
            opacity: '0', pointerEvents: 'none'
        });
        this.video.setAttribute('aria-hidden', 'true');
        this.attachVideo();
        this.video.srcObject = cameraStream;
        // Don't await play(): its promise can stay pending (e.g. autoplay
        // policies); waitForFirstFrame has a timeout instead
        this.video.play().catch((error) => console.warn('[BackgroundBlur] play() failed', error));
        await this.waitForFirstFrame();
        this.throwIfStopped();

        const width = this.video.videoWidth || 1280;
        const height = this.video.videoHeight || 720;

        this.output = createCanvas(width, height);
        this.outputCtx = this.output.getContext('2d');
        this.segmentInput = createCanvas(SEGMENT_WIDTH, SEGMENT_HEIGHT);
        this.segmentInputCtx = this.segmentInput.getContext('2d', { willReadFrequently: true });
        this.mask = createCanvas(SEGMENT_WIDTH, SEGMENT_HEIGHT);
        this.maskCtx = this.mask.getContext('2d');
        this.maskImage = this.maskCtx.createImageData(SEGMENT_WIDTH, SEGMENT_HEIGHT);
        // Feathering at mask resolution is far cheaper than filtering at full size
        this.featheredMask = createCanvas(SEGMENT_WIDTH, SEGMENT_HEIGHT);
        this.featheredMaskCtx = this.featheredMask.getContext('2d');
        this.useFilter = BackgroundBlur.hasCanvasFilter();
        // Without filters the mask isn't feathered; scaling it up to full size
        // with smoothing still gives a soft edge
        if (this.useFilter) this.featheredMaskCtx.filter = `blur(${MASK_FEATHER_PX}px)`;
        this.background = createCanvas(Math.round(width * BLUR_SCALE), Math.round(height * BLUR_SCALE));
        this.backgroundCtx = this.background.getContext('2d');
        if (!this.useFilter) {
            // Successively halved copies of the background, for blurByScaling
            this.scaleLevels = [];
            let w = this.background.width;
            let h = this.background.height;
            for (let i = 0; i < SCALE_BLUR_STEPS; i++) {
                w = Math.max(1, Math.round(w / 2));
                h = Math.max(1, Math.round(h / 2));
                const canvas = createCanvas(w, h);
                const levelCtx = canvas.getContext('2d');
                levelCtx.imageSmoothingEnabled = true;
                this.scaleLevels.push({ canvas, ctx: levelCtx });
            }
        }
        // WebKit (Safari 26.4, Playwright WebKit 18.4) ignores
        // globalCompositeOperation when drawImage's source is a <video>, so
        // the sharp frame covered the blurred background. There the frame is
        // copied to a canvas first and composited from that.
        if (!this.videoCompositingWorks()) {
            this.frame = createCanvas(width, height);
            this.frameCtx = this.frame.getContext('2d');
        }

        // Draw one frame before handing the stream out, so the first frame
        // anyone sees is already blurred
        this.renderFrame();

        this.outputStream = this.output.captureStream(FRAME_RATE);
        const outputTrack = this.outputStream.getVideoTracks()[0];
        // The call code stops tracks to release the camera; make stopping the
        // blurred track do that too
        const stopOutputTrack = outputTrack.stop.bind(outputTrack);
        this.stopOutputTrack = stopOutputTrack;
        outputTrack.stop = () => this.stop();

        // If the camera goes away (unplugged, OS revoked), end the output too
        cameraStream.getVideoTracks().forEach((track) => {
            track.addEventListener('ended', () => this.stop());
        });

        this.timerUrl = URL.createObjectURL(new Blob([TIMER_WORKER_SOURCE], { type: 'text/javascript' }));
        this.timer = new Worker(this.timerUrl);
        this.timer.onmessage = () => this.tick();
        this.timer.postMessage({ interval: FRAME_INTERVAL_MS });
        this.costTrackingSince = performance.now();

        return this.outputStream;
    }

    throwIfStopped() {
        if (this.stopped) throw new Error('Background blur stopped while starting');
    }

    // The hidden video goes on <html>, outside <body>. A StimulusReflex page
    // morph (e.g. Webrtc#state when joining) re-renders <body>'s children and
    // removes anything the server didn't render, and a removed video pauses,
    // which froze the published picture on its last frame.
    attachVideo() {
        document.documentElement.appendChild(this.video);
    }

    // Draws the video with source-in onto an empty canvas: a correct browser
    // draws nothing, as there's nothing to keep the source "in"
    videoCompositingWorks() {
        try {
            const probe = createCanvas(4, 4);
            const probeCtx = probe.getContext('2d', { willReadFrequently: true });
            probeCtx.globalCompositeOperation = 'source-in';
            probeCtx.drawImage(this.video, 0, 0, 4, 4);
            return probeCtx.getImageData(2, 2, 1, 1).data[3] === 0;
        } catch (error) {
            return false; // the canvas copy works either way
        }
    }

    // Backstop in case something else detaches or pauses the hidden video
    keepVideoPlaying() {
        const video = this.video;
        if (!video) return;
        if (!video.isConnected) this.attachVideo();
        if (video.paused && !this.resuming) {
            this.resuming = true;
            video.play()
                .catch((error) => console.warn('[BackgroundBlur] resume play() failed', error))
                .finally(() => { this.resuming = false; });
        }
    }

    waitForFirstFrame() {
        if (this.video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && this.video.videoWidth) {
            return Promise.resolve();
        }
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => reject(new Error('Camera produced no frames')), 5000);
            this.video.addEventListener('loadeddata', () => { clearTimeout(timeout); resolve(); }, { once: true });
        });
    }

    tick() {
        if (this.stopped || this.busy) return;
        const started = performance.now();
        // If frames take longer than the interval, ticks queue up behind them.
        // Drop those rather than rendering back to back, which would starve the
        // rest of the page on a slow device.
        if (started - this.lastFrameEnd < FRAME_INTERVAL_MS / 2) return;
        this.keepVideoPlaying();
        this.busy = true;
        try {
            this.renderFrame();
            this.framesRendered++;
            this.consecutiveErrors = 0;
        } catch (error) {
            console.error('[BackgroundBlur] Frame failed', error);
            if (++this.consecutiveErrors >= 5) this.fallBack('error');
        } finally {
            this.busy = false;
        }
        this.lastFrameEnd = performance.now();
        this.trackCost(this.lastFrameEnd, this.lastFrameEnd - started);
    }

    renderFrame() {
        const video = this.video;
        if (!video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return;
        const { width, height } = this.output;
        const ctx = this.outputCtx;

        // 1. Blurred background, at quarter size. Drawn slightly oversized so
        //    the blur doesn't fade to transparent at the edges.
        const bw = this.background.width;
        const bh = this.background.height;
        if (this.useFilter) {
            const pad = BLUR_RADIUS_PX * 2;
            this.backgroundCtx.filter = `blur(${BLUR_RADIUS_PX}px)`;
            this.backgroundCtx.drawImage(video, -pad, -pad, bw + pad * 2, bh + pad * 2);
        } else {
            this.backgroundCtx.drawImage(video, 0, 0, bw, bh);
            this.blurByScaling();
        }

        // 2. Person mask: segment a small copy of the frame
        this.segmentInputCtx.drawImage(video, 0, 0, SEGMENT_WIDTH, SEGMENT_HEIGHT);
        let timestamp = performance.now();
        if (timestamp <= this.lastTimestamp) timestamp = this.lastTimestamp + 1; // must increase
        this.lastTimestamp = timestamp;
        this.segmenter.segmentForVideo(this.segmentInput, timestamp, (result) => {
            const masks = result.confidenceMasks;
            if (!masks || !masks.length) return;
            // Masks are only valid inside this callback
            const confidence = masks[masks.length - 1].getAsFloat32Array();
            const data = this.maskImage.data;
            for (let i = 0; i < confidence.length; i++) {
                data[i * 4 + 3] = confidence[i] * 255;
            }
            this.maskCtx.putImageData(this.maskImage, 0, 0);
            this.featheredMaskCtx.clearRect(0, 0, SEGMENT_WIDTH, SEGMENT_HEIGHT);
            this.featheredMaskCtx.drawImage(this.mask, 0, 0);
        });

        // 3. Composite: feathered mask (scaled up, which smooths it further)
        //    -> sharp person inside it -> blurred background behind it
        let person = video;
        if (this.frame) {
            this.frameCtx.drawImage(video, 0, 0, width, height);
            person = this.frame;
        }
        ctx.save();
        ctx.globalCompositeOperation = 'copy';
        ctx.imageSmoothingEnabled = true;
        ctx.drawImage(this.featheredMask, 0, 0, width, height);
        ctx.globalCompositeOperation = 'source-in';
        ctx.drawImage(person, 0, 0, width, height);
        ctx.globalCompositeOperation = 'destination-over';
        ctx.drawImage(this.background, 0, 0, width, height);
        ctx.restore();
    }

    // Blur for browsers without canvas filters. Halving in steps averages
    // neighbouring pixels (one big shrink would just skip them and shimmer),
    // and scaling back up in steps with smoothing spreads each remaining pixel
    // softly over its neighbours. All of it runs at no more than 320x180, so it
    // costs far less than the segmentation.
    blurByScaling() {
        const levels = this.scaleLevels;
        let source = this.background;
        for (const level of levels) {
            level.ctx.globalCompositeOperation = 'copy';
            level.ctx.drawImage(source, 0, 0, level.canvas.width, level.canvas.height);
            source = level.canvas;
        }
        for (let i = levels.length - 2; i >= 0; i--) {
            const level = levels[i];
            level.ctx.drawImage(source, 0, 0, level.canvas.width, level.canvas.height);
            source = level.canvas;
        }
        const ctx = this.backgroundCtx;
        ctx.save();
        ctx.globalCompositeOperation = 'copy';
        ctx.imageSmoothingEnabled = true;
        ctx.drawImage(source, 0, 0, this.background.width, this.background.height);
        ctx.restore();
    }

    fallBack(reason) {
        const onFallback = this.onFallback;
        if (!onFallback) return;
        this.onFallback = null; // once
        onFallback(reason);
    }

    trackCost(now, ms) {
        if (!this.onFallback) return;
        this.frameCosts.push([now, ms]);
        while (this.frameCosts.length && now - this.frameCosts[0][0] > OVERLOAD_WINDOW_MS) {
            this.frameCosts.shift();
        }
        // Wait for a full window before judging, so start-up cost doesn't count
        if (now - this.costTrackingSince < OVERLOAD_WINDOW_MS) return;
        const average = this.frameCosts.reduce((sum, [, cost]) => sum + cost, 0) / this.frameCosts.length;
        if (average > OVERLOAD_MS) {
            console.warn(`[BackgroundBlur] Average frame cost ${Math.round(average)}ms, falling back to plain camera`);
            this.fallBack('overload');
        }
    }
}
